import { get, set } from "idb-keyval";

import * as types from "types";

export const CHAT_THREADS_DB_KEY = "chatThreads";
export const DEFAULT_CHAT_THREAD_NAME = "New Chat";
const MESSAGE_KEY_PREFIX = "messages-";

/**
 * Reads chat threads from IndexedDB asynchronously.
 * Returns an empty object if no chat threads are found.
 */
export async function loadChatThreads(): Promise<types.ChatThreads> {
  try {
    const storedData = await get(CHAT_THREADS_DB_KEY);
    return storedData ? storedData : {};
  } catch (error) {
    console.error("Error loading chat threads:", error);
    return {};
  }
}

/**
 * Saves chat threads to IndexedDB asynchronously.
 */
export async function saveChatThreads(
  chatThreads: types.ChatThreads
): Promise<void> {
  try {
    await set(CHAT_THREADS_DB_KEY, chatThreads);
  } catch (error) {
    console.error("Error saving chat threads:", error);
  }
}

/**
 * Saves messages to IndexedDB asynchronously.
 */
export async function saveMessages(
  chatId: string,
  messages: types.Message[]
): Promise<void> {
  try {
    await set(MESSAGE_KEY_PREFIX + chatId, messages);
  } catch (error) {
    console.error(`Error saving messages for chat ${chatId}:`, error);
  }
}

export async function migrateLocalStorageToIdb(): Promise<void> {
  const chatThreadsKey = "chatThreads";
  const chatThreadsLocal = JSON.parse(
    localStorage.getItem(chatThreadsKey) ?? "{}"
  );

  // Check if there are any keys in the object.
  if (!chatThreadsLocal || Object.keys(chatThreadsLocal).length === 0) {
    return;
  }

  const chatThreadsIndexed = await loadChatThreads();

  // Iterate over the chatThreadsLocal object's keys.
  await Promise.all(
    Object.entries(chatThreadsLocal).map(
      async ([id, thread]: [string, any]) => {
        if (!chatThreadsIndexed[id]) {
          const name = thread.name ?? "New Chat";
          const personaId = thread.personaId ?? types.DEFAULT_PERSONA["id"];
          const timestamp = thread.timestamp ?? new Date().toISOString();
          chatThreadsIndexed[id] = {
            id,
            name,
            personaId,
            timestamp,
          };

          // Migrate the messages for this chat thread.
          const messagesKey = `messages-${id}`;
          const messagesLocal = JSON.parse(
            localStorage.getItem(messagesKey) ?? "[]"
          );
          await saveMessages(id, messagesLocal);
        }
      }
    )
  );

  await saveChatThreads(chatThreadsIndexed);

  // Clear the local storage.
  localStorage.removeItem(chatThreadsKey);
  for (const key in localStorage) {
    if (key.startsWith("messages-")) {
      localStorage.removeItem(key);
    }
  }
}
